import React, { useCallback, useState } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useRequests } from '../requests/useRequests'
import { RefreshActions } from '../state/refresh'
// import { InputLabel, Select, MenuItem, FormControl } from '@mui/material'
import { State } from '../types/state'
// import { Role } from '../types/user'
import { Alert, Autocomplete, FormControl } from '@mui/material'
import { SubjectGroup } from '../types/subject-group'
import { InputLabel, Select, MenuItem } from '@mui/material'

interface NewOrEditGroupDialogProps {
  group: SubjectGroup | null
  handleClose: (b: boolean) => void
}

const NewOrEditGroupDialog = ({
  group,
  handleClose
}: NewOrEditGroupDialogProps): JSX.Element => {
  const { subjects, app } = useSelector((state: State) => state.user)
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const [errors, setErrors] = useState<string[]>([])

  const initGroup = {
    id: group?.id,
    name: group?.name,
    subjectGroupProposal: group?.subjectGroupProposal
  }

  const validate = (group: Partial<SubjectGroup>): Partial<SubjectGroup> => {
    const errors: Partial<SubjectGroup> = {}

    setErrors([])

    if (!group?.name || group.name === '') {
      errors.name = 'Name cannot be empty.'
    }

    if (
      ((group?.subjectGroupProposal) == null) ||
      group.subjectGroupProposal.length === 0
    ) {
      errors.subjectGroupProposal = 'Choose at least one subject.' as any
    }

    const subjectIndexes = group.subjectGroupProposal?.map(s => s.index)

    if (
      (subjectIndexes != null) &&
      new Set(subjectIndexes).size !== subjectIndexes?.length
    ) {
      errors.subjectGroupProposal = true as any
      setErrors(e => [...e, 'Subjects must have other indexes'])
    }

    return errors
  }

  const onSubmit = useCallback(
    async (group: Partial<SubjectGroup>): Promise<void> => {
      try {
        const isEdited = group.id !== undefined

        const subjectGroups = {
          ...group,
          subjectGroups:
            group.subjectGroupProposal?.map(s => ({
              subjectId: s.subjectId,
              index: s.index
            })) ?? []
        }

        delete subjectGroups.subjectGroupProposal

        if (isEdited) {
          await requestMaker.updateSubjectGroup(subjectGroups)
        } else {
          await requestMaker.createSubjectGroup(subjectGroups)
        }
        dispatch(RefreshActions.refreshUserState())
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Succesfully update subject group'
          })
        )
        handleClose(true)
      } catch (e) {
        dispatch(
          RefreshActions.setToastMessage({
            message:
              'Error - fill all neccessary fields correctly and try again'
          })
        )
      }
    },
    [requestMaker, dispatch]
  )

  const formik = useFormik({
    initialValues: initGroup,
    onSubmit,
    enableReinitialize: true,
    validate
  })

  return (
    <div>
      <Dialog open onClose={handleClose}>
        <form>
          <DialogTitle>Subject Group</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              error={!!formik.errors.name && formik.touched.name}
              helperText={
                !!formik.errors.name && formik.touched.name
                  ? formik.errors.name
                  : null
              }
              margin='dense'
              id='name'
              label='Name'
              type='name'
              fullWidth
              variant='standard'
              value={formik.values.name}
              onChange={formik.handleChange}
              required
            />
            <FormControl fullWidth style={{ marginTop: '1vw' }}>
              <Autocomplete
                sx={{ m: 1, width: 500 }}
                multiple
                value={
                  formik.values.subjectGroupProposal?.map(s =>
                    subjects.find(sub => s.subjectId === sub.id)
                  ) ?? []
                }
                options={subjects}
                getOptionLabel={option => option!.name}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                disableCloseOnSelect
                onChange={(_, _subjects) => {
                  if (
                    app?.highLevelSubjectNumber &&
                    app?.standardLevelSubjectNumber &&
                    _subjects?.length <=
                      app?.highLevelSubjectNumber +
                        app?.standardLevelSubjectNumber
                  ) {
                    void formik.setFieldValue(
                      'subjectGroupProposal',
                      _subjects.map(s => ({ subjectId: s?.id }))
                    )
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={
                      !!formik.errors.subjectGroupProposal &&
                      formik.touched.subjectGroupProposal
                    }
                    helperText={
                      !!formik.errors.subjectGroupProposal &&
                      formik.touched.subjectGroupProposal
                        ? formik.errors.subjectGroupProposal
                        : null
                    }
                    variant='outlined'
                    label='Subjects'
                    placeholder='Subjects'
                  />
                )}
              />
              {formik.values.subjectGroupProposal?.map((s, key) => (
                <FormControl
                  fullWidth
                  style={{ marginTop: '2vh', marginBottom: '2vh' }}
                >
                  <InputLabel id='index'>
                    {`${
                      subjects.find(sub => sub.id === s.subjectId)?.name
                    }- index`}
                  </InputLabel>
                  <Select
                    labelId='index'
                    id='index'
                    value={s.index}
                    label={`${
                      subjects.find(sub => sub.id === s.subjectId)?.name
                    }- index`}
                    onChange={e => {
                      const currentSubjectGroupProposal = [
                        ...formik.values.subjectGroupProposal!
                      ]
                      const groupProposal = {
                        ...currentSubjectGroupProposal[key]
                      }
                      groupProposal.index = Number(e.target.value)
                      currentSubjectGroupProposal[key] = groupProposal
                      void formik.setFieldValue(
                        'subjectGroupProposal',
                        currentSubjectGroupProposal
                      )
                    }}
                  >
                    {subjects
                      .find(sub => s.subjectId === sub.id)
                      ?.indexes.map(ind => (
                        <MenuItem key={ind.index} value={ind.index}>
                          {ind.index}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              ))}
            </FormControl>
            {errors.map((e, i) => (
              <Alert key={i} severity='error'>
                {e}
              </Alert>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose as any}>Cancel</Button>
            <Button onClick={formik.handleSubmit as any}>Add</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}

export default NewOrEditGroupDialog
